import type { QueryKey } from "@tanstack/react-query";
import type { FlowType } from "../types";

/**
 * The one query-key factory for staff merchant-flow data. Every read and
 * every invalidation (send, resend, invalidate, reset) goes through it so the
 * keys cannot drift between the config, send, and history slots.
 */
export const merchantFlowKeys = {
  all: ["merchantFlows"] as const,
  /** Scope of everything keyed to one deal: list and defaults. */
  borrower: (borrowerId: number) => ["merchantFlows", borrowerId] as const,
  list: (borrowerId: number) => ["merchantFlows", borrowerId, "list"] as const,
  defaults: (borrowerId: number, flowType: FlowType | null | undefined) =>
    ["merchantFlows", borrowerId, "defaults", flowType ?? null] as const,
  /** Every flow type's defaults for this deal. Reads still pass a flow type. */
  defaultsPrefix: (borrowerId: number) => ["merchantFlows", borrowerId, "defaults"] as const,
  /** Every S3 preview for this deal — invalidate after a mutation that changes deal facts. */
  previewPrefix: (borrowerId: number) => ["merchantFlows", borrowerId, "preview"] as const,
  /** Every deal's defaults and previews: a template change can block or unblock any deal. */
  isDealEvaluation: (queryKey: QueryKey) =>
    queryKey[0] === "merchantFlows" &&
    typeof queryKey[1] === "number" &&
    (queryKey[2] === "defaults" || queryKey[2] === "preview"),
  /** S3 draft preview; the config object is part of the key so a draft edit refetches. */
  preview: (
    borrowerId: number,
    flowType: FlowType | null | undefined,
    config: Record<string, unknown>
  ) => ["merchantFlows", borrowerId, "preview", flowType ?? null, config] as const,
  /** This deal's uploads with live Document-only chips. */
  oneOffUploads: (borrowerId: number) => ["merchantFlows", borrowerId, "oneOffUploads"] as const,
  /** Mutation key of an upload or removal of this deal's one-off, pending until the card shows it. */
  oneOffChange: (borrowerId: number) => ["merchantFlows", borrowerId, "oneOffChange"] as const,
  /** Builder JWT for one unsent one-off; minted per open, never shared. */
  oneOffBuilder: (borrowerId: number, documentId: number | null) =>
    ["merchantFlows", borrowerId, "oneOffBuilder", documentId] as const,
  detail: (flowId: number) => ["merchantFlows", "detail", flowId] as const,
  /** The catalog is server data that never changes per deal. */
  documentTypes: () => ["merchantFlows", "documentTypes"] as const,
  documents: (flowId: number) => ["merchantFlows", "documents", flowId] as const,
  /** Every flow's uploaded files, whichever deal (`documents` is keyed by flow alone). */
  documentsPrefix: () => ["merchantFlows", "documents"] as const,
  downloadLinks: (flowId: number) => ["merchantFlows", "downloadLinks", flowId] as const,
} as const;
